import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Maximize2, Minimize2, Play, Pause, RotateCcw, Volume2, VolumeX } from 'lucide-react';

interface CinematicVideoPlayerProps {
  src: string;
  poster?: string;
  label?: string;
  /** Try to autoplay on mount (muted fallback if browser blocks sound). Default true. */
  autoPlay?: boolean;
  /** Scroll the player into view on mount. Default false. */
  scrollIntoView?: boolean;
}

/**
 * Premium cinematic video player for the Living Announcement reveal.
 * - Auto-plays muted on mount, with explicit "Tap to hear" unmute affordance
 * - Tap-to-play/pause, fullscreen, replay
 * - object-contain so faces are never cropped
 * - Shows "Loading your video message…" while buffering
 */
export function CinematicVideoPlayer({
  src,
  poster,
  label = 'A video message is attached',
  autoPlay = true,
  scrollIntoView = false,
}: CinematicVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [ended, setEnded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [showBufferingMessage, setShowBufferingMessage] = useState(false);
  const autoplayAttempted = useRef(false);

  // Video element event wiring
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => { setIsPlaying(true); setEnded(false); setIsBuffering(false); };
    const onPause = () => setIsPlaying(false);
    const onEnded = () => { setIsPlaying(false); setEnded(true); };
    const onWaiting = () => setIsBuffering(true);
    const onCanPlay = () => setIsBuffering(false);
    const onError = () => {
      console.error('[CinematicVideoPlayer] media load error', { src });
      setLoadError(true);
    };
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('ended', onEnded);
    v.addEventListener('waiting', onWaiting);
    v.addEventListener('canplay', onCanPlay);
    v.addEventListener('playing', onCanPlay);
    v.addEventListener('error', onError);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('ended', onEnded);
      v.removeEventListener('waiting', onWaiting);
      v.removeEventListener('canplay', onCanPlay);
      v.removeEventListener('playing', onCanPlay);
      v.removeEventListener('error', onError);
    };
  }, [src]);

  // Show buffering message only if still buffering after 3s
  useEffect(() => {
    if (!isBuffering) { setShowBufferingMessage(false); return; }
    const t = setTimeout(() => setShowBufferingMessage(true), 3000);
    return () => clearTimeout(t);
  }, [isBuffering]);

  // Autoplay on mount (muted by default — browsers allow this)
  useEffect(() => {
    if (!autoPlay || autoplayAttempted.current) return;
    autoplayAttempted.current = true;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    setIsMuted(true);
    const tryPlay = async () => {
      try {
        await v.play();
      } catch (e) {
        console.warn('[CinematicVideoPlayer] autoplay blocked', e);
      }
    };
    tryPlay();
  }, [autoPlay]);

  // Scroll into view on mount so the recipient notices it
  useEffect(() => {
    if (!scrollIntoView) return;
    const t = setTimeout(() => {
      wrapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 400);
    return () => clearTimeout(t);
  }, [scrollIntoView]);

  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  }, []);

  const unmute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setIsMuted(false);
    if (v.paused) v.play().catch(() => {});
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const el = wrapRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await el.requestFullscreen();
    } catch (e) {
      console.warn('[CinematicVideoPlayer] fullscreen unavailable', e);
    }
  }, []);

  const replay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play().catch(() => {});
  }, []);

  if (loadError) {
    return (
      <div className="px-4 py-6 max-w-2xl mx-auto">
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-center text-sm text-destructive">
          The attached video couldn't be loaded. <a href={src} target="_blank" rel="noreferrer" className="underline">Open in new tab</a>.
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="px-3 sm:px-4 pt-3 pb-8 max-w-3xl mx-auto"
      initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: 0.55, delay: 0.12, ease: [0.22, 0.61, 0.36, 1] }}
    >
      <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground text-center mb-3">
        🎬 {label}
      </p>
      <div
        ref={wrapRef}
        className="relative group rounded-2xl overflow-hidden bg-black shadow-[0_24px_60px_-20px_rgba(0,0,0,0.55)] ring-1 ring-white/10"
      >
        <div className="relative w-full" style={{ aspectRatio: isFullscreen ? undefined : '16 / 10' }}>
          <video
            ref={videoRef}
            src={src}
            poster={poster}
            playsInline
            muted
            preload="auto"
            controls={false}
            onClick={togglePlay}
            className="absolute inset-0 w-full h-full object-contain bg-black cursor-pointer"
          />

          {/* Buffering overlay (after 3s of waiting) */}
          <AnimatePresence>
            {showBufferingMessage && isBuffering && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center bg-black/55 text-white/90 text-sm"
              >
                Loading your video message…
              </motion.div>
            )}
          </AnimatePresence>

          {/* Tap-to-hear pill when muted-autoplaying */}
          <AnimatePresence>
            {isPlaying && isMuted && (
              <motion.button
                type="button"
                onClick={unmute}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 text-black text-sm font-medium shadow-lg hover:bg-white"
              >
                <VolumeX className="h-4 w-4" />
                Tap to hear the video message
              </motion.button>
            )}
          </AnimatePresence>

          {/* Center play affordance when paused/ended */}
          <AnimatePresence>
            {(!isPlaying && !isBuffering) && (
              <motion.button
                type="button"
                onClick={ended ? replay : togglePlay}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.35 }}
                className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                aria-label={ended ? 'Replay video' : 'Play video'}
              >
                <span className="h-16 w-16 rounded-full bg-white/95 text-black flex items-center justify-center shadow-2xl">
                  {ended ? <RotateCcw className="h-7 w-7" /> : <Play className="h-7 w-7 ml-1" />}
                </span>
              </motion.button>
            )}
          </AnimatePresence>

          {/* Top-right controls */}
          <div className="absolute top-3 right-3 flex gap-2 opacity-90">
            <button
              type="button"
              onClick={toggleMute}
              aria-label={isMuted ? 'Unmute' : 'Mute'}
              className="h-9 w-9 rounded-full bg-black/55 backdrop-blur text-white flex items-center justify-center hover:bg-black/75 transition-colors"
            >
              {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              className="h-9 w-9 rounded-full bg-black/55 backdrop-blur text-white flex items-center justify-center hover:bg-black/75 transition-colors"
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
          </div>

          {/* Bottom-left mini controls when playing */}
          {isPlaying && (
            <div className="absolute bottom-3 left-3 flex gap-2">
              <button
                type="button"
                onClick={togglePlay}
                aria-label="Pause"
                className="h-9 w-9 rounded-full bg-black/55 backdrop-blur text-white flex items-center justify-center hover:bg-black/75 transition-colors"
              >
                <Pause className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
